
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Session } from '@supabase/supabase-js';

export const useAuthSession = () => {
  const { data } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        // Clear previous console logs to avoid confusion
        console.log('Fetching auth session...');
        
        // First check if we have a session in localStorage to avoid network request
        const localSession = localStorage.getItem('supabase.auth.token');
        if (!localSession) {
          console.log('No session found in localStorage');
        }
        
        // Get the session from Supabase
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error fetching session:', error);
          // Handle specific error cases
          if (error.message && error.message.includes('Auth session missing')) {
            console.log('No valid auth session found - clearing any stale data');
            // Clean up any local storage tokens that might be invalid
            localStorage.removeItem('supabase.auth.token');
            
            // Clear any other auth-related items in local storage
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.includes('supabase.auth')) {
                localStorage.removeItem(key);
              }
            }
          }
          return null;
        }
        
        if (!data.session) {
          console.log('No active session found from API');
          return null;
        }
        
        // Validate session token expiry
        const now = Math.floor(Date.now() / 1000);
        if (data.session.expires_at && data.session.expires_at < now) {
          console.log('Session has expired, expires_at:', data.session.expires_at, 'now:', now);
          
          // Attempt to refresh token if needed
          try {
            console.log('Attempting to refresh session...');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.error('Error refreshing session:', refreshError);
              // Clear any potentially stale tokens
              localStorage.removeItem('supabase.auth.token');
              localStorage.removeItem('supabase.auth.refreshToken');
              return null;
            }
            
            if (refreshData && refreshData.session) {
              console.log('Session refreshed successfully, new expiry:', refreshData.session.expires_at);
              return refreshData.session;
            } else {
              console.log('Session refresh returned no data');
              return null;
            }
          } catch (refreshErr) {
            console.error('Unexpected error during refresh:', refreshErr);
            return null;
          }
        }
        
        console.log('Valid session found for user:', data.session.user.id);
        return data.session as Session;
      } catch (err) {
        console.error('Unexpected error during session fetch:', err);
        return null;
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60 * 5, // 5 minutes 
    retry: 1, // Minimal retry to avoid excessive requests on failure
    refetchInterval: 1000 * 60 * 10, // Check session every 10 minutes
    refetchOnMount: true,
  });

  return data || null;
};
